import React, { useEffect } from "react";
import styled, { css } from "styled-components";
import { motion } from "framer-motion";
import { FaDownload, FaPrint } from "react-icons/fa";

// Метатеги
const MetaTags = () => (
  <>
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <meta name="description" content="Реквизиты и квитанция для ООО «Сонет-Дом». Полная информация о компании и возможность скачивания квитанции." />
    <meta name="keywords" content="Сонет-Дом, реквизиты, квитанция, ООО, скачать, печать" />
  </>
);

// Глобальные стили для предотвращения горизонтального скроллинга
const GlobalStyles = styled.div`
  width: 100%;
  overflow-x: hidden;
`;

// Стиль для секции
const Section = styled(motion.section)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: #f4f4f4;
  color: #333;
  transition: background 0.5s ease;
  h1, p, button {
    z-index: 1;
    position: relative;
    text-align: center;
  }
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

// Заголовок
const Title = styled.h1`
  font-size: 3rem;
  margin-bottom: 2rem;
  color: #007aff;
  animation: fadeIn 1s ease-in-out;
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @media (max-width: 768px) {
    font-size: 2.5rem;
    margin-bottom: 1.5rem;
  }
`;

// Контейнер для информации о реквизитах
const RequisitesContainer = styled(motion.div)`
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  width: 90%;
  max-width: 600px;
  margin: 1rem;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  padding: 2rem;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
  }
  @media (max-width: 768px) {
    width: 100%;
    padding: 1.5rem;
  }
`;

// Подзаголовок
const Subtitle = styled.h2`
  font-size: 2rem;
  margin-bottom: 1rem;
  color: #333;
  @media (max-width: 768px) {
    font-size: 1.75rem;
    margin-bottom: 0.75rem;
  }
`;

// Информация о реквизитах
const RequisitesInfo = styled.p`
  font-size: 1rem;
  color: #555;
  margin-bottom: 1rem;
  line-height: 1.5;
  @media (max-width: 768px) {
    font-size: 0.9rem;
    margin-bottom: 1rem;
  }
`;

// Контейнер для квитанции
const InvoiceContainer = styled(motion.div)`
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  width: 90%;
  max-width: 700px;
  margin: 1rem;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  padding: 2rem;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
  }
  @media (max-width: 768px) {
    width: 100%;
    padding: 1.5rem;
  }
`;

// Кнопка для скачивания и печати
const Button = styled.button`
  background: #007aff;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  margin: 0.5rem;
  cursor: pointer;
  transition: background 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  &:hover {
    background: #005cbf;
  }
  @media (max-width: 768px) {
    padding: 0.6rem 1.25rem;
  }
`;

const Requisites = () => {
  const sectionVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } },
  };

  const requisitesVariants = {
    hidden: { opacity: 0, y: 50, scale: 1 },
    visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.6, ease: "easeOut" } },
    hover: { scale: 1.02, transition: { duration: 0.3, ease: "easeInOut" } },
  };

  const invoiceVariants = {
    hidden: { opacity: 0, y: 50, scale: 1 },
    visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.6, ease: "easeOut" } },
    hover: { scale: 1.02, transition: { duration: 0.3, ease: "easeInOut" } },
  };

  const downloadInvoice = () => {
    const link = document.createElement("a");
    link.href = "/invoice/kvitanciya.xlsx";
    link.download = "kvitanciya.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const printIframeContent = () => {
    const iframe = document.getElementById('invoice-iframe');
    if (iframe && iframe.contentWindow) {
      const content = iframe.contentWindow.document.body.innerHTML;
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <html>
          <head>
            <title>Квитанция</title>
          </head>
          <body>
            ${content}
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    }
  };

  // Эффект для прокрутки страницы вверх при загрузке
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <MetaTags />
      <GlobalStyles>
        <Section
          variants={sectionVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          bgColor="#f4f4f4"
          textColor="#333"
          style={{ marginTop: '60px' }} // Добавляем отступ сверху
        >
          <Title>Реквизиты</Title>

          <RequisitesContainer
            variants={requisitesVariants}
            initial="hidden"
            whileInView="visible"
            whileHover="hover"
            viewport={{ once: true }}
          >
            <header>
              <Subtitle>Общество с Ограниченной Ответственностью «Сонет-Дом»</Subtitle>
            </header>
            <article>
              <RequisitesInfo>Полное наименование: Общество с ограниченной ответственностью «Сонет-Дом»</RequisitesInfo>
              <RequisitesInfo>Сокращённое наименование: ООО «Сонет-Дом»</RequisitesInfo>
              <RequisitesInfo>Юридический адрес: 141606 Московская область, г. Клин, ул. Мира, д. 25, офис 9</RequisitesInfo>
              <RequisitesInfo>ИНН 5020062886</RequisitesInfo>
              <RequisitesInfo>КПП 502001001</RequisitesInfo>
              <RequisitesInfo>ОГРН 1105020001448</RequisitesInfo>
              <RequisitesInfo>ОКПО 66129445</RequisitesInfo>
              <RequisitesInfo>ОКТМО 46621101</RequisitesInfo>
              <RequisitesInfo>ОКВЭД 64.20.3</RequisitesInfo>
              <RequisitesInfo>Расчетный счет № 40702810301290001129</RequisitesInfo>
              <RequisitesInfo>Кор/счет № 30101810145250000411</RequisitesInfo>
              <RequisitesInfo>БИК 044525411 в филиале «Центральный» Банка ВТБ (ПАО) г. Москвы</RequisitesInfo>
              <RequisitesInfo>Тел.: 8 (495) 994-13-98, 8 (496) 26 4-90-45</RequisitesInfo>
              <RequisitesInfo>E-mail: mail@sonet-dom.ru</RequisitesInfo>
              <RequisitesInfo>Генеральный директор: Степанченко Сергей Викторович</RequisitesInfo>
            </article>
          </RequisitesContainer>

          <Title>Квитанция</Title>

          <InvoiceContainer
            variants={invoiceVariants}
            initial="hidden"
            whileInView="visible"
            whileHover="hover"
            viewport={{ once: true }}
          >
            <iframe
              id="invoice-iframe"
              src="/invoice/kvitanciya.htm"
              title="Квитанция"
              width="100%"
              height="680px"
              style={{ border: 'none' }}
              onLoad={() => {
                const iframe = document.getElementById('invoice-iframe');
                if (iframe && iframe.contentWindow) {
                  iframe.contentWindow.addEventListener('load', () => {
                    console.log('Iframe loaded');
                  });
                }
              }}
            />
          </InvoiceContainer>

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '2rem' }}>
            <Button onClick={downloadInvoice}>
              <FaDownload /> Скачать квитанцию
            </Button>
          </div>
        </Section>
      </GlobalStyles>
    </>
  );
};

export default Requisites;